<template>
  <div class="flex items-center justify-between w-full h-10 gap-1">
    <!-- Left content: Show count -->
    <div class="flex items-center h-full">
      <span class="text-sm text-black tracking-[0.25px] leading-5 font-normal" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">
        Show {{ startItem }} to {{ endItem }} of {{ totalItems }} Notes
      </span>
    </div>

    <!-- Main pagination controls -->
    <div class="flex-1 flex items-center justify-center gap-6">
      <!-- Previous button -->
      <button
        :disabled="currentPage === 1"
        class="flex items-center justify-center w-6 h-6 rounded-full bg-[#e4f3f5] hover:bg-[#c5e3e7] transition disabled:opacity-50 disabled:cursor-not-allowed"
        @click="goToPage(currentPage - 1)"
      >
        <img src="@/assets/encounterNotes/chevron_left.svg" alt="Previous" class="w-4 h-4" />
      </button>

      <!-- Page numbers -->
      <div class="flex items-center gap-3">
        <button
          v-for="page in visiblePages"
          :key="page"
          :class="pageButtonClasses(page)"
          @click="typeof page === 'number' && goToPage(page)"
          :disabled="page === '...'"
        >
          {{ page }}
        </button>
      </div>

      <!-- Next button -->
      <button
        :disabled="currentPage === totalPages"
        class="flex items-center justify-center w-6 h-6 rounded-full bg-[#e4f3f5] hover:bg-[#c5e3e7] transition disabled:opacity-50 disabled:cursor-not-allowed"
        @click="goToPage(currentPage + 1)"
      >
        <img src="@/assets/encounterNotes/chevron_right.svg" alt="Next" class="w-4 h-4" />
      </button>
    </div>

    <!-- Per page selector -->
    <div class="relative">
      <button
        class="flex items-center gap-2 h-10 px-4 py-2 bg-white border border-[#c4c4c4] rounded text-sm text-[#333] tracking-[0.15px] leading-[18px] font-light hover:border-[#999] transition"
        style="font-family: 'Inter', sans-serif;"
        @click="showDropdown = !showDropdown"
      >
        {{ perPage }} per page
        <img
          src="@/assets/encounterNotes/chevron_down.svg"
          alt="Dropdown"
          class="w-[18px] h-[18px] transition-transform"
          :class="{ 'rotate-180': showDropdown }"
        />
      </button>

      <div
        v-if="showDropdown"
        class="absolute bottom-full left-0 mb-1 w-full bg-white border border-[#c4c4c4] rounded shadow-lg z-10"
      >
        <button
          v-for="option in perPageOptions"
          :key="option"
          class="w-full px-4 py-2 text-left text-sm text-[#333] hover:bg-[#f9fbfd] transition"
          @click="selectPerPage(option)"
        >
          {{ option }} per page
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  currentPage: {
    type: Number,
    default: 1
  },
  perPage: {
    type: Number,
    default: 10
  },
  totalItems: {
    type: Number,
    default: 100
  }
});

const emit = defineEmits(['update:currentPage', 'update:perPage']);

const showDropdown = ref(false);
const perPageOptions = [5, 10, 20, 50];

const totalPages = computed(() => Math.ceil(props.totalItems / props.perPage));

const startItem = computed(() => {
  return (props.currentPage - 1) * props.perPage + 1;
});

const endItem = computed(() => {
  return Math.min(props.currentPage * props.perPage, props.totalItems);
});

const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = props.currentPage;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);

    if (current > 3) {
      pages.push('...');
    }

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    for (let i = start; i <= end; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    if (current < total - 2) {
      pages.push('...');
    }

    if (!pages.includes(total)) {
      pages.push(total);
    }
  }

  return pages;
});

const pageButtonClasses = (page) => {
  const base = 'flex items-center justify-center min-w-[24px] h-6 px-2 py-1 rounded-[17px] text-sm tracking-[0.4px] font-normal';
  
  if (page === '...') {
    return `${base} text-[#333] cursor-default`;
  }
  
  if (page === props.currentPage) {
    return `${base} bg-[#004879] text-white`;
  }
  
  return `${base} text-[#333] hover:bg-[#e4f3f5] transition cursor-pointer`;
};

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    emit('update:currentPage', page);
  }
};

const selectPerPage = (option) => {
  emit('update:perPage', option);
  showDropdown.value = false;
};
</script>

<style scoped>
button {
  font-family: 'Roboto', sans-serif;
}
</style>
